import React, { useState } from 'react';
import { useDisclosure } from '@mantine/hooks';
import { Modal } from '@mantine/core';
import { css, useTheme } from '@emotion/react';
import { Icon } from '../../Icon/Icon';
import { globals, handleDLMiniCartClick, TrendingProductItems } from 'common-ui';
import { useRecoilValue } from 'recoil';
import { selectCartData } from 'common-state';
import type { MiniCartModalProps } from 'tsconfig/types';
import MiniCartLayout from '../MiniCartLayout';
import TaxMessage from './components/TaxMessage';
import { useMiniCartLogic } from './hooks/useMiniCartLogic';

const Title = () => {
  let titleText = 'In your cart';

  if (globals.country === 'uk') {
    titleText = 'Added to basket';
  } else if (globals.brandId === 'newstf') {
    titleText = 'Added to cart';
  }

  return (
    <div className="mini-cart-header">
      <Icon kind="tick-circle" size="l" />
      <h3>{titleText}</h3>
    </div>
  );
};

const MiniCartModal: React.FC<MiniCartModalProps> = ({
  show,
  size = 'xl',
  onClose,
  selectedItemCode,
  qty,
}: MiniCartModalProps) => {
  const theme = useTheme();
  const [opened, { close }] = useDisclosure(false);
  const { cartItems } = useRecoilValue(selectCartData);
  const [animationClass, setAnimationClass] = useState('slide-in');
  const [currentMiniCartItemCode, setCurrentMiniCartItemCode] = useState<string>('');

  const { selectedItem, showCheckoutButton, isFreeGiftLineItem, isGreatPick } = useMiniCartLogic(
    cartItems,
    selectedItemCode,
    currentMiniCartItemCode,
    setCurrentMiniCartItemCode,
  );

  const MiniCartWrapper = css`
    ${theme.widgets.Cart.MiniCart || ''}
    .mantine-Modal-content {
      animation: ${animationClass === 'slide-in' ? 'slideInFromRight' : 'slideOutToRight'} 0.2s ease-in-out !important;
    }
  `;

  const handleClose = () => {
    setAnimationClass('slide-out');
    const timer = setTimeout(() => {
      close();
      onClose();
    }, 200);
    return () => clearTimeout(timer);
  };

  const handleMiniCartClickEvents = (eventType: string) => {
    eventType && handleDLMiniCartClick('miniCartClick', eventType);
  };

  if (!show) {
    return null;
  }

  return selectedItem?.length ? (
    <Modal
      css={MiniCartWrapper}
      opened={opened || !!show}
      className={`mantine-Modal-content-minicart vine-react-app ${animationClass}`}
      onClose={() => {
        handleClose();
        handleMiniCartClickEvents('closeMiniCartClick');
      }}
      title={<Title />}
      size={size}
    >
      <div style={{ position: 'relative' }}>
        <MiniCartLayout
          lineItems={selectedItem}
          selectedItemCode={selectedItemCode}
          totalLineItem={cartItems?.totalItemQuantity ?? 0}
          onClose={handleClose}
          isFreeGiftLineItem={isFreeGiftLineItem}
          isGreatPick={isGreatPick}
          qty={qty}
          showCheckoutButton={showCheckoutButton}
        />
        {globals.country !== 'us' && (
          <TrendingProductItems showImageZoom={false} isMiniCart={true} widgetTitle="Top Trending Wines" />
        )}
      </div>
      <button
        className="continue-shopping desktop button-link"
        data-testid="continue-shopping"
        onClick={() => {
          handleClose();
          handleMiniCartClickEvents('continueShoppingtMiniCartClick');
        }}
      >
        <span className="continue-shopping-text">continue shopping</span>
      </button>
      <TaxMessage selectedItem={selectedItem} />
    </Modal>
  ) : null;
};

export default MiniCartModal;
